// Recordable memory windows for achievement authoring (spec 7.2, 8.4): RAM
// only, no mirrors, echo RAM or ROM. The memory recorder records exactly these
// bytes, and an authored trigger or score definition may read only inside them,
// with every byte of each read inside one window. Addresses use the same
// rcheevos space as the trigger DSL (see src/shared/ra-address-windows.js).
import { SIZE_BYTE_WIDTHS } from './ra-address-windows.js';

const windows = (...ranges) => Object.freeze(ranges.map((range) => Object.freeze(range)));

// GB and GBC are one family (src/shared/system-merge.js): WRAM bank 0 and 1,
// the CGB banks 2-7 in rcheevos' banked addressing, and HRAM through readIO.
const GB_FAMILY = windows([0xC000, 0xDFFF], [0x10000, 0x15FFF], [0xFF80, 0xFFFE]);

export const RECORDABLE_WINDOWS = Object.freeze({
  nes: windows([0x0000, 0x07FF], [0x6000, 0x7FFF]),
  snes: windows([0x000000, 0x01FFFF]),
  gb: GB_FAMILY,
  gbc: GB_FAMILY,
  gba: windows([0x00000, 0x07FFF], [0x08000, 0x47FFF]),
  genesis: windows([0x0000, 0xFFFF]),
  atari2600: windows([0x00, 0x7F]),
  c64: windows([0x0000, 0xFFFF]),
});

const hasWindows = (systemId) => typeof systemId === 'string'
  && Object.prototype.hasOwnProperty.call(RECORDABLE_WINDOWS, systemId);

/** The frozen window list for a system; RangeError for n64 or an unknown system. */
export function recordableWindowsFor(systemId) {
  if (!hasWindows(systemId)) {
    const name = typeof systemId === 'string' ? systemId : typeof systemId;
    throw new RangeError(`no recordable windows for system ${name} (n64 is out of version 1)`);
  }
  return RECORDABLE_WINDOWS[systemId];
}

/** Bytes in one sample: every window, concatenated in table order. */
export function recordableSnapshotBytes(systemId) {
  return recordableWindowsFor(systemId).reduce((sum, [lo, hi]) => sum + (hi - lo + 1), 0);
}

/** Offset of `addr` in the concatenated snapshot, or -1 when it is not recorded. */
export function snapshotOffset(systemId, addr) {
  const ranges = recordableWindowsFor(systemId);
  if (!Number.isSafeInteger(addr)) return -1;
  let base = 0;
  for (const [lo, hi] of ranges) {
    if (addr >= lo && addr <= hi) return base + (addr - lo);
    base += hi - lo + 1;
  }
  return -1;
}

/** Bytes read for a DSL size name; a missing size is u8. RangeError otherwise. */
export function sizeByteWidth(size = 'u8') {
  if (typeof size !== 'string' || !Object.prototype.hasOwnProperty.call(SIZE_BYTE_WIDTHS, size)) {
    throw new RangeError(`unknown trigger size ${typeof size === 'string' ? size : typeof size}`);
  }
  return SIZE_BYTE_WIDTHS[size];
}

/** True only when addr through addr + byteWidth - 1 lies inside one window. */
export function isSpanRecordable(systemId, addr, byteWidth) {
  if (!hasWindows(systemId)) return false;
  if (!Number.isSafeInteger(addr) || addr < 0 || !Number.isSafeInteger(byteWidth) || byteWidth < 1) return false;
  const last = addr + byteWidth - 1;
  return RECORDABLE_WINDOWS[systemId].some(([lo, hi]) => addr >= lo && last <= hi);
}
